'use strict'
import * as React from 'react'
import { useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Link as RouterLink } from 'react-router-dom'
import { Link } from '@mui/material'
import 'react-datepicker/dist/react-datepicker.css'
import { RefleshButton } from '../common/CustomButton'
import { getDynamoDB } from '../../common/utils/DynamoDbUtils'
import { formatDateHHmmdd, getSystemDatetime } from '../../common/utils/DateUtils'
import { viewId, titleName, table, masterClassification, masterStatus } from '../../_constants/Code'
import { useToast } from '../../_components/Toast'
import { errorLog } from '../../common/logger/Logger'
import CustomTooltip from '../../_components/CustomTooltip'
import Forbidden from '../../_components/Forbidden'

const masterListTemplate = [
  {
    MasterId: viewId.groupCompanyInfo,
    Url: '/master/group/groupCompanyInfo',
    Outline: 'CHROFYを利用していない企業も含むグループ会社一覧を管理します',
    AuthKey: 'GroupCompanyInfo_Set'
  },
  {
    MasterId: viewId.segmentInfo,
    Url: '/master/group/segmentInfo',
    Outline: 'グループ全体の事業セグメントの有効期間等の情報を管理します',
    AuthKey: 'SegmentInfo_Set'
  },
  {
    MasterId: viewId.associationInfo,
    Url: '/master/org/associationInfo',
    Outline: '組織の第1階層の関連付けを管理します',
    AuthKey: 'DepartmentHierarchy_Set',
    Classification: masterClassification.department.value
  },
  {
    MasterId: viewId.depInfoHierarchySetting,
    Url: '/master/org/depInfoHierarchySetting',
    Outline: '組織情報を管理します',
    AuthKey: 'DepartmentData_Set',
    Classification: masterClassification.department.value
  },
  {
    MasterId: viewId.certificationInfo,
    Url: '/master/dashboard/certificationInfo',
    Outline: '資格の種類や属性を管理します',
    AuthKey: 'CertificationMaster_Set'
  },
  {
    MasterId: viewId.certificationSet,
    Url: '/master/dashboard/certificationSet',
    Outline: '複数資格をまとめて管理する資格セットを設定します',
    AuthKey: 'CertificationSet_Set'
  },
  {
    MasterId: viewId.newGraduateInfo,
    Url: '/master/dashboard/newGraduateInfo',
    Outline: '人員構成シミュレーションに使用する新卒採用に関する設定値を管理します',
    AuthKey: 'NewGraduate_Set'
  },
  {
    MasterId: viewId.recruitmentPlanInfo,
    Url: '/master/dashboard/recruitmentPlanInfo',
    Outline: '年度ごとの採用予定人数を管理します',
    AuthKey: 'RecruitmentPlan_Set'
  },
  {
    MasterId: viewId.absenceRateInfo,
    Url: '/master/dashboard/absenceRateInfo',
    Outline: '高欠勤と判断するための欠勤率の閾値を管理します',
    AuthKey: 'AbsenceRate_Set'
  },
  {
    MasterId: viewId.securitiesReportInfo,
    Url: '/master/dashboard/securitiesReportInfo',
    Outline: '有価証券報告書レポートの出力に関する設定を管理します',
    AuthKey: 'SecuritiesReport_Set'
  },
  {
    MasterId: viewId.employeeCountInfo,
    Url: '/master/dashboard/employeeCountInfo',
    Outline: 'ヘッドカウントの人数カウントルールを設定します',
    AuthKey: 'EmployeeCount_Set'
  },
  {
    MasterId: viewId.annualIncomeCalculationInfo,
    Url: '/master/dashboard/annualIncomeCalculationInfo',
    Outline: '年収計算の集計対象や、集計除外する条件を設定します',
    AuthKey: 'AnnualIncomeCalculation_Set'
  },
  {
    MasterId: viewId.statutoryDisclosure,
    Url: '/master/dashboard/statutoryDisclosure',
    Outline: '法定開示が必須となっているダッシュボードの表示設定をします',
    AuthKey: 'StatutoryDisclosure_Set'
  },
  {
    MasterId: viewId.categoryInfo,
    Url: '/master/other/categoryInfo',
    Outline: 'ダッシュボードに設定する用途タグを設定します',
    AuthKey: 'CategoryTag_Set'
  },
  {
    MasterId: viewId.companyInfo,
    Url: '/master/company/companyInfo',
    Outline: '会社情報を管理します',
    AuthKey: 'CompanyInfo_Set'
  },
  {
    MasterId: viewId.conversionTableInfo,
    Url: '/master/dataMapping/conversionTableInfo',
    Outline: '変換表を設定します',
    AuthKey: 'ConversionTable_Set'
  },
  {
    MasterId: viewId.userClassificationInfo,
    Url: '/master/dataMapping/userClassificationInfo',
    Outline: '各区分の貴社設定値を管理します',
    AuthKey: 'UserClassificationItem_Set'
  },
  {
    MasterId: viewId.chrofyClassificationInfo,
    Url: '/master/dataMapping/chrofyClassificationInfo',
    Outline: 'CHROFY規定の区分設定が確認できます。規定値の参照のみ可能です',
    AuthKey: 'ChrofyClassificationItem_Set'
  },
  {
    MasterId: viewId.judgmentRuleList,
    Url: '/master/dataMapping/judgmentRuleList',
    Outline: 'データの判定に使用するルールを設定・管理します',
    AuthKey: 'JudgmentRule_Set'
  }
]

export const CompanyTypeEnum = Object.freeze({
  STANDALONE: 'STANDALONE', //単独
  SUB: 'SUB', // 子会社
  SUPER: 'SUPER' //親会社
})

export const useGetCompanyType = ({ corporationNumber }) => {
  const [hasAnalysisTarget, setHasAnalysisTarget] = React.useState(false)
  const [isParent, setIsParent] = React.useState(false)

  React.useEffect(() => {
    ;(async () => {
      try {
        const analysisTargetP = getDynamoDB(table.AnalysisTargetInfo, null, corporationNumber)
        const companyP = getDynamoDB(table.Company, null, corporationNumber)
        const [analysisTarget, [companyData]] = await Promise.all([analysisTargetP, companyP])

        setHasAnalysisTarget(Array.isArray(analysisTarget) && analysisTarget?.length > 0)
        setIsParent(companyData?.SuperCorporationNumber === '')
      } catch (error) {
        setHasAnalysisTarget(false)
        setIsParent(false)
      }
    })()
  }, [corporationNumber])

  //親会社と分析対象がない
  //単独の場合親判定ですが、こちらの条件だけ変転します

  if (!isParent)
    return CompanyTypeEnum.SUB //子会社
  else if (!hasAnalysisTarget)
    return CompanyTypeEnum.STANDALONE //単独会社
  else if (hasAnalysisTarget)
    return CompanyTypeEnum.SUPER // 親会社
  else return null // 不正
}

const MasterListContent = (props) => {
  const [isLoaded, setIsLoaded] = React.useState(false)
  const [groupList, setGroupList] = React.useState([])
  const [companyList, setCompanyList] = React.useState([])
  const [departmentList, setDepartmentList] = React.useState([])
  const [dashboardList, setDashboardList] = React.useState([])
  const [otherList, setOtherList] = React.useState([])
  const [empAttributeList, setEmpAttributeList] = React.useState([])
  const [classificationList, setClassificationList] = React.useState([])
  const [certificationList, setCertificationList] = React.useState([])
  const [dataMappingList, setDataMappingList] = React.useState([])
  const [lastFetchDatetime, setLastFetchDatetime] = React.useState()
  const authConf = props.authority.Master_AuthInfo.Auth_Conf
  const authConfCategory = props.authority.CategoryTag_AuthInfo.Auth_Conf

  const companyType = useGetCompanyType(props)
  const isSingleCompany = companyType === CompanyTypeEnum.STANDALONE && companyType !== null

  React.useEffect(() => {
    document.title = props.pageTitle
    init()
    if (props.message) {
      props.showToast(props.message)
    }
  }, [])

  const _getMaster = async () => {
    try {
      let master = await getDynamoDB(table.Master, null, props.corporationNumber, null, null)

      return master
    } catch (error) {
      errorLog('E000001', props.userId, viewId.master, 'getMaster', error)
    }
  }

  /** 権限のないマスターは画面表示しないようにフィルターする */
  const _filterAuthMaster = (list, classification) => {
    return list.filter((item) => {
      const master = masterListTemplate.find((m) => m.MasterId === item.MasterId)
      if (item.MasterId === viewId.segmentInfo && !props.isParent) return false
      return master && authConf?.[classification]?.[master.AuthKey] === true
    })
  }

  /** MasterId から表示名を取得（テンプレート由来の合成行用） */
  const _getMasterDisplayName = (masterId) => {
    const key = Object.keys(viewId).find((k) => viewId[k] === masterId)
    return key ? titleName[key] : masterId
  }

  /** 組織関連マスターに、DB に未登録だがテンプレート・権限がある項目をマージする */
  const _mergeDepartmentTemplate = (authDepartment) => {
    const departmentTemplateItems = masterListTemplate.filter(
      (t) => t.Classification === masterClassification.department.value && authConf?.Department?.[t.AuthKey] === true
    )
    const existingIds = new Set(authDepartment.map((m) => m.MasterId))
    const synthetic = departmentTemplateItems
      .filter((t) => !existingIds.has(t.MasterId))
      .map((t) => ({
        MasterId: t.MasterId,
        MasterName: _getMasterDisplayName(t.MasterId),
        Classification: masterClassification.department.value,
        LatestSettingFiscalYear: '-',
        UpdateDateTime: '0000-00-00 00:00:00',
        UpdateUserName: '-',
        Status: masterStatus.notset.value
      }))
    return [...authDepartment, ...synthetic].sort((a, b) => a.MasterId.localeCompare(b.MasterId))
  }

  /** 権限のないその他関係は画面表示しないようにフィルターする */
  const _filterAuthOther = (list, classification) => {
    return list.filter((item) => {
      const master = masterListTemplate.find((m) => m.MasterId === item.MasterId)
      if (item.MasterId === viewId.segmentInfo && !props.isParent) return false
      return master && authConfCategory?.[master.AuthKey] === true
    })
  }

  const _setMaster = async () => {
    try {
      // マスター情報取得
      let master = await _getMaster()
      master = master.sort((a, b) => a.MasterId.localeCompare(b.MasterId))

      if (master.length) {
        // グループ会社関連マスター
        // 親会社でない、もしくは権限を持っていない場合は表示しない
        const group = master.filter((m) => m.Classification == masterClassification['group'].value)
        const authGroup = _filterAuthMaster(group, 'Group')
        setGroupList(authGroup)

        // 会社関連マスター
        const company = master.filter((m) => m.Classification == masterClassification['company'].value)
        const authCompany = _filterAuthMaster(company, 'Company')
        setCompanyList(authCompany)

        // 組織関連マスター（DB に無くてもテンプレート・権限があれば表示）
        const department = master.filter((m) => m.Classification == masterClassification['department'].value)
        const authDepartment = _filterAuthMaster(department, 'Department')
        setDepartmentList(_mergeDepartmentTemplate(authDepartment))

        // 従業員属性マスター
        const empAttribute = master.filter((m) => m.Classification == masterClassification['empAttribute'].value)
        const authEmpAttribute = _filterAuthMaster(empAttribute, 'Emp_Attribute')
        setEmpAttributeList(authEmpAttribute)

        // 区分関連マスター（データマッピングから userClassificationInfo / chrofyClassificationInfo を分離）
        const dataMapping = master.filter((m) => m.Classification == masterClassification['dataMapping'].value)
        const authDataMapping = _filterAuthMaster(dataMapping, 'DataMapping')
        const classificationIds = [viewId.userClassificationInfo, viewId.chrofyClassificationInfo]
        setClassificationList(authDataMapping.filter((m) => classificationIds.includes(m.MasterId)))
        setDataMappingList(authDataMapping.filter((m) => !classificationIds.includes(m.MasterId)))

        // 資格関連マスター
        const certification = master.filter((m) => m.Classification == masterClassification['certification'].value)
        const authCertification = _filterAuthMaster(certification, 'Certification')
        setCertificationList(authCertification)

        // ダッシュボード設定
        const dashboard = master.filter((m) => m.Classification == masterClassification['dashboard'].value)
        const authDashboard = _filterAuthMaster(dashboard, 'Dashboard')
        setDashboardList(authDashboard)

        // その他設定
        const other = master.filter((m) => m.Classification == masterClassification['other'].value)
        const authOther = _filterAuthOther(other, 'Other')
        setOtherList(authOther)
      } else {
        // DB にマスターが無くても、組織関連はテンプレート・権限があれば表示
        setDepartmentList(_mergeDepartmentTemplate([]))
      }
      setLastFetchDatetime(formatDateHHmmdd(getSystemDatetime()))
    } catch (error) {
      errorLog('E000001', props.userId, viewId.master, 'setMaster', error)
    }
  }

  const init = async () => {
    await _setMaster()
    setIsLoaded(true)
  }

  const BaseTable = ({ list, testId = '' }) => {
    if (list.length) {
      return (
        <div className="setting-table is-master" data-testid={testId}>
          <table className="setting-table__table table-fixed w-full">
            <thead>
              <tr>
                <th className="setting-table__col-master-name">機能名</th>
                <th className="setting-table__col-master-Outline">機能概要</th>
                <th className="setting-table__col-master-Latest-setting-fiscal-year">最新設定年度</th>
                <th className="setting-table__col-master-last-update-date">最終更新日時</th>
                <th className="setting-table__col-master-last-update-user">最終更新者</th>
                <th className="setting-table__col-master-status">ステータス</th>
              </tr>
            </thead>
            <tbody className="text-caption-1">
              {list.map((master, idx) => {
                const t = masterListTemplate.find((t) => t.MasterId == master.MasterId)
                return (
                  <tr key={idx} data-testid={master.MasterName}>
                    <td>
                      <Link variant="button" underline="none" component={RouterLink} to={t.Url}>
                        <span style={{ fontSize: '16px', textTransform: 'none' }}>{master.MasterName}</span>
                      </Link>
                    </td>
                    <td>
                      <CustomTooltip title={t.Outline} useParentWidth={true}>
                        {(ref) => (
                          <div ref={ref} className="truncate">
                            <span>{t.Outline} </span>
                          </div>
                        )}
                      </CustomTooltip>
                    </td>
                    <td>{master?.LatestSettingFiscalYear || '-'}</td>
                    <td>{master.UpdateDateTime.slice(0, -3)}</td>
                    <td>{master.UpdateUserName}</td>
                    <td>{masterStatus[master.Status].name}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )
    } else {
      return (
        <div className="text-center my-10">
          <span className="text-h5 red--text">登録可能なマスター情報がありません。</span>
        </div>
      )
    }
  }

  /** 描画処理 */
  if (!isLoaded) {
    return null
  }
  return (
    <React.Fragment>
      <main className="page-container">
        <section className="page-content">
          <div className="centered-container">
            {/* コンテンツヘッダ */}
            <header className="page-header flex justify-center py-1">
              <h1 className="page-header__name text-h1 grey--text py-8">{titleName.masterList}</h1>
            </header>
            {/*  コンテンツボディ */}
            <div className="page-body">
              {/*  ===== リード ===== */}
              <p className="text-body text-center mb-10">
                CHROFYで利用する各種マスターを管理します。必要に応じて設定値を変更してください。
              </p>
              {/*  ===== /リード ===== */}
              <div className="box-xy-bordered rounded-t-3xl rounded-b-2xl pt-10 pb-16 px-16 border-blue1">
                <h2 className="box-bordered-heading blue1 white--text text-h3 font-medium pt-2.5 pb-3 px-14 -mt-10 -mx-16 rounded-t-2xl">
                  マスター一覧
                </h2>
                <div className="flex mt-4">
                  <div className="base-table-header div-mid mb-1">
                    <span className="text-caption-2 pr-2">{lastFetchDatetime} 時点</span>
                    <div className="btn-group">
                      <RefleshButton
                        className="btn header-btn small-btn white--text flex"
                        name="一覧更新"
                        onClick={() => _setMaster()}
                      />
                    </div>
                  </div>
                </div>
                {/*  ===== エラーメッセージ表示===== */}
                <div className="error-messages">
                  {props.errorMessages &&
                    Object.keys(props.errorMessages).flatMap((key, keyIdx) =>
                      props.errorMessages[key].map((msg, msgIdx) => (
                        <span key={`${key}-${msgIdx}`} className="red--text">
                          {(keyIdx !== 0 || msgIdx !== 0) && <br />}
                          {msg.errorMessage}
                        </span>
                      ))
                    )}
                </div>
                {/*  ===== マスター一覧 ===== */}
                {/*  ===== グループ会社関連マスター =====たた単独でない場合のみ表示 */}
                {!isSingleCompany && (
                  <React.Fragment>
                    <div className="flex items-center justify-between my-6 mb-2">
                      <div className="text-body-2-medium grow">
                        <div>■{masterClassification.group.name}</div>
                      </div>
                    </div>
                    <BaseTable list={groupList} />
                  </React.Fragment>
                )}
                {/*  ===== /グループ会社関連マスター ===== */}

                {/*  ===== 会社関連マスター ===== */}
                <div className="flex items-center justify-between my-6 mb-2">
                  <div className="text-body-2-medium grow">
                    <div>■{masterClassification.company.name}</div>
                  </div>
                </div>
                <BaseTable list={companyList} />
                {/*  ===== /会社関連マスター ===== */}

                {/*  ===== 組織関連マスター ===== */}
                <div className="flex items-center justify-between my-6 mb-2">
                  <div className="text-body-2-medium grow">
                    <div>■{masterClassification.department.name}</div>
                  </div>
                </div>
                <BaseTable list={departmentList} />
                {/*  ===== /組織関連マスター ===== */}

                {/*  ===== 区分関連マスター ===== */}
                <div className="flex items-center justify-between my-6 mb-2">
                  <div className="text-body-2-medium grow">
                    <div>■{masterClassification.classification.name}</div>
                  </div>
                </div>
                <BaseTable list={classificationList} />
                {/*  ===== /区分関連マスター ===== */}

                {/*  ===== データマッピング関連マスター ===== */}
                <div className="flex items-center justify-between my-6 mb-2">
                  <div className="text-body-2-medium grow">
                    <div>■{masterClassification.dataMapping.name}</div>
                  </div>
                </div>
                <BaseTable list={dataMappingList} />
                {/*  ===== /データマッピング関連マスター ===== */}

                {/*  ===== ダッシュボード設定 ===== */}
                <div className="flex items-center justify-between my-6 mb-2">
                  <div className="text-body-2-medium grow">
                    <div>■{masterClassification.dashboard.name}</div>
                  </div>
                </div>
                <BaseTable list={dashboardList} />
                {/*  ===== /ダッシュボード設定 ===== */}

                {/*  ===== 資格関連マスター===== */}
                <div className="flex items-center justify-between my-6 mb-2">
                  <div className="text-body-2-medium grow">
                    <div>■{masterClassification.certification.name}</div>
                  </div>
                </div>
                <BaseTable list={certificationList} />
                {/*  ===== /資格関連マスター===== */}

                {/*  ===== その他設定 ===== */}
                <div className="flex items-center justify-between my-6 mb-2">
                  <div className="text-body-2-medium grow">
                    <div>■{masterClassification.other.name}</div>
                  </div>
                </div>
                <BaseTable list={otherList} />
                {/*  ===== /その他設定 ===== */}
              </div>
            </div>
          </div>
        </section>
      </main>
    </React.Fragment>
  )
}

const MasterList = () => {
  const state = useSelector((state) => state)
  const location = useLocation()
  const showToast = useToast()

  // 権限を持っていない場合は画面表示しない
  if (!state.authority.Master_AuthInfo.Availability) {
    return <Forbidden />
  }

  return (
    <MasterListContent
      pageTitle={titleName.masterList + titleName.common}
      corporationNumber={state.company.corporationNumber}
      userId={state.user.sub}
      isParent={state.company.isParent}
      message={location.state?.message}
      errorMessages={location.state?.errorMessages}
      showToast={showToast}
      authority={state.authority}
    />
  )
}

export default MasterList
